// session 管理：与 A 阶段 Express 后端契约保持一致
// - 登录后 xyt_login RPC 返回 { session_token, username, role, expires_at }
// - R26 起：后端 Set-Cookie(HttpOnly, SameSite=Lax) 写入 xyt_session_token，
//   客户端 JS 读不到，浏览器 credentials:include 自动带去后端；XSS 偷不走 token。
//   响应体仍返回 session_token 是为了兼容 legacy xyt-sdk.js（HTML 版前端）。
// - 同时把 username/role/expires_at 存在非 httpOnly 的 "xyt_session_info" cookie，
//   供客户端 JS 读取（不敏感，只有显示名之类）

export const SESSION_TOKEN_COOKIE = "xyt_session_token";
export const SESSION_INFO_COOKIE = "xyt_session_info";
// Cookie 最长 30 天（实际 session 生命周期由后端滑动过期 + revoked_at 管控，
// 这里只是把 bearer 放久一点，避免"早上登录晚上 cookie 过期 → 莫名退出"）。
// 后端会在用户每次请求时 extend expires_at；不活跃超过 SESSION_HOURS（默认 12h）才失效。
export const SESSION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

export interface SessionInfo {
  username: string;
  role: "管理" | "编辑" | "填写" | "查看" | string;
  expiresAt: string; // ISO 字符串
  displayName?: string;
  employeeId?: string;
  mustChangePassword?: boolean;
}

// --- 客户端：读 session info cookie ---
export function readSessionInfoClient(): SessionInfo | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${SESSION_INFO_COOKIE}=([^;]+)`));
  if (!m) return null;
  try {
    return JSON.parse(decodeURIComponent(m[1])) as SessionInfo;
  } catch {
    return null;
  }
}

// --- 客户端：写 session info cookie（由 login 成功后调用）---
export function writeSessionInfoClient(info: SessionInfo) {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(JSON.stringify(info));
  const maxAge = SESSION_MAX_AGE_SECONDS;
  document.cookie = `${SESSION_INFO_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

// --- 客户端：清除（登出）---
export function clearSessionClient() {
  if (typeof document === "undefined") return;
  document.cookie = `${SESSION_INFO_COOKIE}=; path=/; max-age=0`;
  document.cookie = `${SESSION_TOKEN_COOKIE}=; path=/; max-age=0`;
}

// R26 起 token cookie 是 HttpOnly，document.cookie 读不到 → 此函数返回 null。
// 保留签名是为了让现有 hooks（use-users / use-workflow 等）里的
// `p_session_token: readSessionTokenClient()` 继续编译通过 —— 后端 RPC 写法
// 都是 `body.p_session_token || req.sessionToken`，null 会回退到 cookie 路径，
// 链路无缝。Socket.IO 走 withCredentials:true 带 cookie，也不再需要此函数。
export function readSessionTokenClient(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${SESSION_TOKEN_COOKIE}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}
